import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { isPasskeyCancelled, isPasskeySupported } from '../utils/webauthn';
import { apiErrorMessage } from '../utils/forms/apiErrors';

const LoginPage = () => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [passkeyLoading, setPasskeyLoading] = useState(false);
  const { login, loginWithPasskey } = useAuth();
  const navigate = useNavigate();

  // Hidden rather than shown-and-broken: over plain http on the farm LAN the browser exposes no
  // credentials API at all, and the password form is the only way in there.
  const passkeysAvailable = isPasskeySupported();

  const handlePasskeyLogin = async () => {
    setError('');
    setPasskeyLoading(true);
    try {
      await loginWithPasskey();
      navigate('/');
    } catch (err) {
      if (!isPasskeyCancelled(err)) {
        setError(apiErrorMessage(err, 'Nie udało się zalogować kluczem dostępu.'));
      }
    } finally {
      setPasskeyLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await login(username, password);
      navigate('/');
    } catch (err) {
      if (err.response?.status === 401 || err.response?.status === 403) {
        setError('Nieprawidłowa nazwa użytkownika lub hasło.');
      } else {
        setError('Błąd połączenia z serwerem. Spróbuj ponownie.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-screen">
      <div style={{
        width: '100%',
        maxWidth: '400px',
      }}>
        {/* Logo / Header */}
        <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
          <div style={{
            width: '72px',
            height: '72px',
            borderRadius: '18px',
            background: 'linear-gradient(135deg, #4a7c59, #2c5530)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto 1rem',
            boxShadow: '0 8px 24px rgba(74, 124, 89, 0.4)',
          }}>
            <i className="fas fa-seedling" style={{ fontSize: '2rem', color: '#fff' }}></i>
          </div>
          <h1 style={{ color: '#fff', fontSize: '1.75rem', fontWeight: '700', marginBottom: '0.25rem' }}>
            Farm Manager
          </h1>
          <p style={{ color: '#a0aec0', fontSize: '0.9rem' }}>
            Zaloguj się, aby kontynuować
          </p>
        </div>

        {/* Login Card */}
        <div style={{
          background: 'rgba(255,255,255,0.05)',
          backdropFilter: 'blur(10px)',
          border: '1px solid rgba(255,255,255,0.1)',
          borderRadius: '16px',
          padding: '2rem',
          boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
        }}>
          <form onSubmit={handleSubmit}>
            {error && (
              <div style={{
                background: 'rgba(229, 62, 62, 0.15)',
                border: '1px solid rgba(229, 62, 62, 0.3)',
                borderRadius: '8px',
                padding: '0.75rem 1rem',
                marginBottom: '1.25rem',
                color: '#fc8181',
                fontSize: '0.875rem',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
              }}>
                <i className="fas fa-exclamation-circle"></i>
                {error}
              </div>
            )}

            <div style={{ marginBottom: '1.25rem' }}>
              <label style={{ color: '#e2e8f0', fontSize: '0.875rem', fontWeight: '500', display: 'block', marginBottom: '0.5rem' }}>
                Nazwa użytkownika
              </label>
              <div style={{ position: 'relative' }}>
                <span style={{
                  position: 'absolute', left: '0.875rem', top: '50%', transform: 'translateY(-50%)',
                  color: '#718096', fontSize: '0.875rem',
                }}>
                  <i className="fas fa-user"></i>
                </span>
                <input
                  type="text"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  required
                  autoFocus
                  autoComplete="username"
                  style={{
                    width: '100%',
                    padding: '0.75rem 0.875rem 0.75rem 2.5rem',
                    background: 'rgba(255,255,255,0.07)',
                    border: '1px solid rgba(255,255,255,0.15)',
                    borderRadius: '8px',
                    color: '#e2e8f0',
                    outline: 'none',
                    transition: 'border-color 0.2s',
                    boxSizing: 'border-box',
                  }}
                  onFocus={(e) => e.target.style.borderColor = '#4a7c59'}
                  onBlur={(e) => e.target.style.borderColor = 'rgba(255,255,255,0.15)'}
                  placeholder="admin"
                />
              </div>
            </div>

            <div style={{ marginBottom: '1.5rem' }}>
              <label style={{ color: '#e2e8f0', fontSize: '0.875rem', fontWeight: '500', display: 'block', marginBottom: '0.5rem' }}>
                Hasło
              </label>
              <div style={{ position: 'relative' }}>
                <span style={{
                  position: 'absolute', left: '0.875rem', top: '50%', transform: 'translateY(-50%)',
                  color: '#718096', fontSize: '0.875rem',
                }}>
                  <i className="fas fa-lock"></i>
                </span>
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  autoComplete="current-password"
                  style={{
                    width: '100%',
                    padding: '0.75rem 0.875rem 0.75rem 2.5rem',
                    background: 'rgba(255,255,255,0.07)',
                    border: '1px solid rgba(255,255,255,0.15)',
                    borderRadius: '8px',
                    color: '#e2e8f0',
                    outline: 'none',
                    transition: 'border-color 0.2s',
                    boxSizing: 'border-box',
                  }}
                  onFocus={(e) => e.target.style.borderColor = '#4a7c59'}
                  onBlur={(e) => e.target.style.borderColor = 'rgba(255,255,255,0.15)'}
                  placeholder="••••••••"
                />
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              style={{
                width: '100%',
                padding: '0.875rem',
                background: loading ? 'rgba(74, 124, 89, 0.5)' : 'linear-gradient(135deg, #4a7c59, #2c5530)',
                border: 'none',
                borderRadius: '8px',
                color: '#fff',
                fontSize: '1rem',
                fontWeight: '600',
                cursor: loading ? 'not-allowed' : 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '0.5rem',
                transition: 'opacity 0.2s, transform 0.1s',
                boxShadow: loading ? 'none' : '0 4px 15px rgba(74, 124, 89, 0.4)',
              }}
            >
              {loading ? (
                <>
                  <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                  Logowanie...
                </>
              ) : (
                <>
                  <i className="fas fa-sign-in-alt"></i>
                  Zaloguj się
                </>
              )}
            </button>
          </form>

          {passkeysAvailable && (
            <>
              <div style={{
                display: 'flex', alignItems: 'center', gap: '0.75rem',
                margin: '1.5rem 0', color: 'rgba(255,255,255,0.4)', fontSize: '0.85rem',
              }}>
                <div style={{ flex: 1, height: '1px', background: 'rgba(255,255,255,0.15)' }} />
                lub
                <div style={{ flex: 1, height: '1px', background: 'rgba(255,255,255,0.15)' }} />
              </div>

              <button
                type="button"
                onClick={handlePasskeyLogin}
                disabled={loading || passkeyLoading}
                style={{
                  width: '100%',
                  padding: '0.9rem',
                  background: 'transparent',
                  color: '#e2e8f0',
                  border: '1px solid rgba(255,255,255,0.25)',
                  borderRadius: '10px',
                  fontSize: '1rem',
                  fontWeight: 500,
                  cursor: (loading || passkeyLoading) ? 'not-allowed' : 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '0.5rem',
                }}
              >
                {passkeyLoading ? (
                  <>
                    <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                    Oczekiwanie na klucz...
                  </>
                ) : (
                  <>
                    <i className="fas fa-fingerprint"></i>
                    Zaloguj kluczem dostępu
                  </>
                )}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
